/**
 * @fileoverview Mock backend for the organisation details endpoints.
 * @description Registers GET and PUT /sales/organisation-details on the feature's shared mock
 * adapter, answering in the backend's envelopes with its error codes (Confluence 3.5.11.25-.26):
 * a read of details never saved is 409/4120; a save is Super Admin only, needs the `od1.` version it
 * read as a strong `If-Match` (428/4104 when missing once saved, 412/4122 when stale) except the very
 * first save, and validates the required fields. The record starts saved, with obviously fictional
 * sample values, so Send Payment Details works out of the box in mock mode. Held in memory only; a
 * reload restores the sample. Exports the helpers the Send Payment Details mock and the tests
 * use. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { mockCaller } from './sales-crm-mock-projection'
import { bodyOf, failReply, headerOf, okEnvelope } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'

import type { MockReply } from './sales-crm-mock-response.utils'
import type {
  OrganisationDetails,
  OrganisationDetailsInput,
} from '@/features/sales/types/my-page.types'
import type { AxiosRequestConfig } from 'axios'

/** @description The sample details the mock starts with (fictional, never a real account). */
const SAMPLE_DETAILS: OrganisationDetails = {
  legalName: 'Example Software Private Limited',
  tradeName: 'ExampleSoft',
  cin: 'U72900MH2020PTC123456',
  gstin: '27AAAAA0000A1Z5',
  pan: 'AAAAA0000A',
  registeredAddress: '1 Example Road, Andheri East',
  city: 'Mumbai',
  state: 'Maharashtra',
  pincode: '400001',
  billingContactName: 'Billing Desk',
  billingEmail: 'billing@example.test',
  billingPhone: '9876543210',
  supportEmail: 'support@example.test',
  bank: {
    beneficiaryName: 'Example Software Private Limited',
    bankName: 'Example Bank',
    branch: 'Andheri East',
    accountNumber: '123456789012',
    ifsc: 'EXMP0000123',
    accountType: 'CURRENT',
  },
  updatedAt: '2026-10-31T09:00:00.000Z',
  version: 'od1.1',
}

/** @description Every optional field cleared, so a save that leaves one out clears it. */
const SAMPLE_BLANK = {
  tradeName: null,
  cin: null,
  gstin: null,
  pan: null,
  registeredAddress: null,
  city: null,
  state: null,
  pincode: null,
  billingContactName: null,
  billingEmail: null,
  billingPhone: null,
  supportEmail: null,
}

/** @description The saved details, or null before the first save. */
let saved: OrganisationDetails | null = { ...SAMPLE_DETAILS }

/** @description How many times the details have been saved, for minting versions. */
let saveCount = 1

/**
 * @description Whether the organisation details have been saved.
 * @returns {boolean} True once they exist.
 */
export function hasOrganisationDetails(): boolean {
  return saved !== null
}

/**
 * @description Restores the mock to its starting state, or to "never saved" for a test of the first
 * save and of 409/4120.
 * @param {boolean} [withSample] - Start with the sample details saved (the default) or empty.
 */
export function resetOrganisationDetailsMock(withSample = true): void {
  saved = withSample ? { ...SAMPLE_DETAILS } : null
  saveCount = withSample ? 1 : 0
}

/**
 * @description Reads an `If-Match` header's version, without its entity-tag quotes.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {string | undefined} The version, if the header was sent.
 */
function ifMatchOf(config: AxiosRequestConfig): string | undefined {
  return headerOf(config, 'If-Match')?.replace(/^"|"$/g, '')
}

/**
 * @description Answers Get Organisation Details: the saved details with their version as the ETag,
 * or 409/4120 when they were never saved.
 * @returns {MockReply} The details or the refusal.
 */
function getReply(): MockReply {
  if (!saved) return failReply(409, '4120', 'The organisation details have not been saved')
  return [200, okEnvelope(saved), { ETag: `"${saved.version}"` }]
}

/**
 * @description Whether a save's body has the required fields: a legal name and the six bank fields.
 * @param {Record<string, unknown>} body - The request body.
 * @returns {boolean} True when they are all present.
 */
function hasRequiredFields(body: Record<string, unknown>): boolean {
  const bank = body.bank as Record<string, unknown> | undefined
  const required = ['beneficiaryName', 'bankName', 'branch', 'accountNumber', 'ifsc', 'accountType']
  return (
    typeof body.legalName === 'string' &&
    body.legalName.trim() !== '' &&
    bank !== undefined &&
    required.every((key) => typeof bank[key] === 'string' && bank[key] !== '')
  )
}

/**
 * @description Answers Update Organisation Details: a Super Admin creates (first save) or replaces
 * the details; a field left out is cleared.
 * @param {AxiosRequestConfig} config - The request config, for the body and `If-Match`.
 * @returns {MockReply} The saved details or the refusal.
 */
function putReply(config: AxiosRequestConfig): MockReply {
  if (mockCaller()?.role !== 'Super Admin') {
    return failReply(422, '2008', 'Only a Super Admin may update the organisation details')
  }
  const version = ifMatchOf(config)
  if (saved && version === undefined) {
    return failReply(428, '4104', 'If-Match is required')
  }
  if (version !== undefined && version !== saved?.version) {
    return failReply(412, '4122', 'The organisation details changed since they were read')
  }
  const body = bodyOf(config)
  if (!hasRequiredFields(body)) return failReply(400, '0001', 'Validation failed')
  const input = body as unknown as OrganisationDetailsInput
  saveCount += 1
  saved = {
    ...SAMPLE_BLANK,
    ...input,
    updatedAt: new Date().toISOString(),
    version: `od1.${String(saveCount)}`,
  }
  return [200, okEnvelope(saved), { ETag: `"${saved.version}"` }]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/organisation-details').reply(getReply)
  salesMockAdapter.onPut('/sales/organisation-details').reply(putReply)
}
